/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * useFillViewportHeight - size an element to fill from its current position to the bottom of the
 * viewport, so tables/lists/detail panes use the vertical space instead of rendering short in
 * document flow (leaving an empty band below) or scrolling while space is wasted elsewhere.
 *
 * The height is measured from the element's own top offset, so it auto-adapts to whatever chrome
 * (header, filters, tabs) sits above it on any page — no per-page magic numbers. The element then
 * scrolls its own overflow internally (pair with a `flex flex-col min-h-0` child, e.g.
 * KnowledgeNodeTable's `fillHeight`).
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useCallback, useLayoutEffect, useState } from 'react'

/**
 * @param {import('react').RefObject<HTMLElement>} ref - element to size
 * @param {Object} [options]
 * @param {number} [options.bottomGap=24] - px to leave below the element (breathing room / footer)
 * @param {number} [options.minHeight=320] - never shrink below this (small viewports)
 * @param {boolean} [options.enabled=true] - when false, returns null so the caller keeps document flow
 * @returns {number|null} the pixel height to apply (via inline style), or null when disabled/unmeasured
 */
export const useFillViewportHeight = (
  ref,
  { bottomGap = 24, minHeight = 320, enabled = true } = {}
) => {
  const [height, setHeight] = useState(null)

  const recompute = useCallback(() => {
    const el = ref.current
    if (!el) return
    // Measure the element's NATURAL top (as if the page were scrolled to the top) by adding the
    // current scroll offset to its viewport-relative top. Using the raw viewport top would corrupt
    // the height whenever the window happens to be scrolled when we measure (e.g. right after a tab
    // switch), producing a wildly wrong height. This form is scroll-independent and stable.
    const rect = el.getBoundingClientRect()
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0
    const naturalTop = rect.top + scrollY
    const viewportH = window.visualViewport?.height ?? window.innerHeight
    // Reserve the app footer so a pinned footer stays visible below the filled region (and its own
    // pagination isn't hidden behind it). Falls back to bottomGap when there's no footer.
    const footer = typeof document !== 'undefined' ? document.querySelector('footer') : null
    const reserve = footer ? Math.max(bottomGap, footer.offsetHeight + 8) : bottomGap
    const next = Math.max(minHeight, Math.round(viewportH - naturalTop - reserve))
    setHeight((prev) => (prev === next ? prev : next))
  }, [ref, bottomGap, minHeight])

  useLayoutEffect(() => {
    if (!enabled) {
      setHeight(null)
      return undefined
    }

    let observer
    let rafId
    let tries = 0

    // The ref'd element may not exist when this effect first runs — it can mount later (async data
    // load, or a table behind an inner sub-tab). Poll briefly until it appears, then measure it and
    // observe it (re-measure on its own resize) plus document.body (chrome above it changing).
    const attach = () => {
      const el = ref.current
      if (!el) {
        if (tries++ < 180) rafId = requestAnimationFrame(attach)
        return
      }
      recompute()
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(recompute)
        observer.observe(el)
        if (typeof document !== 'undefined' && document.body) observer.observe(document.body)
      }
    }
    attach()

    window.addEventListener('resize', recompute)
    const vv = window.visualViewport
    vv?.addEventListener('resize', recompute)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', recompute)
      vv?.removeEventListener('resize', recompute)
      observer?.disconnect()
    }
  }, [enabled, recompute, ref])

  return enabled ? height : null
}

export default useFillViewportHeight
